import { DEFAULT_CURRENCY } from '@procureai/schemas';
import { DefaultValues } from 'react-hook-form';
import { z } from 'zod';

import { Currency, TsrctQuoteItemUpdatableStatus, TsrctQuoteMode } from '@/generated/gql/graphql';

const quotePaymentTerm = z.object({
  daysToPayment: z.number().min(0),
  discount: z.number().min(0).max(100),
});

const quoteLineItemSchema = z.object({
  id: z.string().nullable(),
  position: z.string().min(1),
  title: z.string().min(1),
  description: z.string().nullable(),
  unitId: z.string().min(1),
  deliveryLocationId: z.string().nullish(),
  organizationId: z.string().nullish(),
  quantity: z.number().positive(),
  pricePerUnit: z.number().min(0).nullish(),
  shipmentTerms: z.object({
    earlyDeliveryAt: z.date().nullable(),
    lateDeliveryAt: z.date().nullable(),
  }),
  totalValue: z.number().min(0).nullish(),
  status: z.enum(TsrctQuoteItemUpdatableStatus),
});

const optionalSupplierReferenceQuoteModes = new Set([TsrctQuoteMode.BID_SHEET, TsrctQuoteMode.BID_SHEET_PROPOSAL]);
const isOptionalSupplierReference = (quoteMode: TsrctQuoteMode | undefined) =>
  quoteMode && optionalSupplierReferenceQuoteModes.has(quoteMode);

const supplierReference = z.string().trim().min(1);
// allows null (before text field modification) or empty string (after text field modification)
const optionalSupplierReference = z.union([supplierReference, z.literal(null), z.literal('')]);

export const quoteFormDataSchema = (quoteMode?: TsrctQuoteMode) =>
  z.object({
    quote: z.object({
      id: z.string().nullable(),
      currency: z.enum(Currency),
      supplierReference: isOptionalSupplierReference(quoteMode) ? optionalSupplierReference : supplierReference,
      paymentTerms: z.array(quotePaymentTerm),
      lineItems: z.array(quoteLineItemSchema),
    }),
  });

// creating a specific schema for type generation
export const interalSchema = quoteFormDataSchema();
export type QuoteFormData = z.infer<typeof interalSchema>;

export const quoteFormDataDefault: DefaultValues<QuoteFormData> = {
  quote: {
    id: '',
    currency: DEFAULT_CURRENCY as Currency,
    lineItems: [],
    paymentTerms: [],
  },
};

const numberMapper = z
  .string()
  .nullable()
  .transform<number | undefined>((val) => (val ? Number(val) : undefined));

const TsrctQuoteLineItemShipmentTermsMapper = z.object({
  earlyDeliveryAt: z
    .string()
    .transform((val) => (val ? new Date(val as string) : null))
    .nullable(),
  lateDeliveryAt: z
    .string()
    .transform((val) => (val ? new Date(val as string) : null))
    .nullable(),
});

export const TsrctQuoteLineItemMapper = z
  .array(
    z.looseObject({
      quantity: numberMapper,
      pricePerUnit: numberMapper,
      totalValue: numberMapper,
      shipmentTerms: TsrctQuoteLineItemShipmentTermsMapper,
    }),
  )
  .prefault([]);

export const TsrctPaymentTermsMapper = z
  .array(
    quotePaymentTerm
      .extend({
        discount: z.string().transform((val) => Number(val)),
      })
      .passthrough(),
  )
  .prefault([]);

export const TsrctQuoteMapper = z.object({
  quote: z.looseObject({
    lineItems: TsrctQuoteLineItemMapper,
    paymentTerms: TsrctPaymentTermsMapper,
  }),
});
